import { HttpContext, HttpContextToken } from '@angular/common/http';

export class CacheSettings {
  lastUpdated?: Date;
  expires?: number | Date;
  update?: boolean;
}

/** Whether this request participates in the app's explicit response cache. */
export const HTTP_CACHE_ENABLED = new HttpContextToken<boolean>(() => false);

/** Per-request cache settings; meaningful only when HTTP_CACHE_ENABLED is true. */
export const HTTP_CACHE_SETTINGS = new HttpContextToken<CacheSettings | null>(() => null);

/** Skip the default toast/log error handler while still allowing auth handling. */
export const HTTP_SKIP_ERROR_HANDLER = new HttpContextToken<boolean>(() => false);

/** Keep an absolute/custom URL unchanged instead of applying the tenant API prefix. */
export const HTTP_DISABLE_API_PREFIX = new HttpContextToken<boolean>(() => false);

/** Do not attach the app OAuth access token. Plugin callers can still set their own Authorization header. */
export const HTTP_DISABLE_ACCESS_TOKEN = new HttpContextToken<boolean>(() => false);

/** Do not run the 401 re-authentication / 403 forbidden handler for this request. */
export const HTTP_DISABLE_AUTHORIZATION = new HttpContextToken<boolean>(() => false);

/**
 * Transitional compatibility flag for the historical HttpService.disableLoading() API.
 * LoadingInterceptor is not part of the current effective HttpService chain, so the
 * modern provider switch must not accidentally make loading behavior newly active.
 */
export const HTTP_DISABLE_LOADING = new HttpContextToken<boolean>(() => false);

export interface HypecastHttpContextOptions {
  cache?: CacheSettings | null;
  cacheEnabled?: boolean;
  skipErrorHandler?: boolean;
  disableApiPrefix?: boolean;
  disableAccessToken?: boolean;
  disableAuthorization?: boolean;
  disableLoading?: boolean;
}

/**
 * Builds the typed per-request metadata consumed by the Angular 21 functional
 * interceptor chain. Keeping this in HttpContext avoids mutable HttpClient
 * subclasses and lets multiple flags compose on one normal HttpClient request.
 */
export function hypecastHttpContext(options: HypecastHttpContextOptions = {}): HttpContext {
  let context = new HttpContext();

  if (options.cacheEnabled === true || Object.prototype.hasOwnProperty.call(options, 'cache')) {
    context = context.set(HTTP_CACHE_ENABLED, true);
    context = context.set(HTTP_CACHE_SETTINGS, options.cache ?? null);
  }
  if (options.skipErrorHandler) {
    context = context.set(HTTP_SKIP_ERROR_HANDLER, true);
  }
  if (options.disableApiPrefix) {
    context = context.set(HTTP_DISABLE_API_PREFIX, true);
  }
  if (options.disableAccessToken) {
    context = context.set(HTTP_DISABLE_ACCESS_TOKEN, true);
  }
  if (options.disableAuthorization) {
    context = context.set(HTTP_DISABLE_AUTHORIZATION, true);
  }
  if (options.disableLoading) {
    context = context.set(HTTP_DISABLE_LOADING, true);
  }

  return context;
}
